/**
 * @file Generic Photo Viewer.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { HotKeys } from 'react-hotkeys';
import get from 'lodash.get';
import classNames from 'classnames';

import ArrowLeft from '../icons/ArrowLeft';
import Alert from '../global/Alert';
import { getImageSources } from '../../util/photo';
import { IMAGE_SIZE_CODE } from '../../constants/image-size-codes';

const LOAD_MORE_THRESHOLD = 5;

class PhotoViewer extends Component {
  static propTypes = {
    photoId: PropTypes.string.isRequired,
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        thumbnail_urls: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        ),
        watermarked_urls: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        ),
      }),
    ).isRequired,
    children: PropTypes.node,
    navigationPath: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        pathname: PropTypes.string,
        state: PropTypes.shape({
          basePath: PropTypes.string.isRequired,
          step: PropTypes.number,
        }),
      }),
    ]).isRequired,
    preferWatermarked: PropTypes.bool,
    maxSize: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
    getAdjacentPhotoId: PropTypes.func,
    forceImageSize: PropTypes.bool,
    history: PropTypes.shape({
      replace: PropTypes.func,
    }),
    loadMore: PropTypes.func,
    totalPhotos: PropTypes.number,
    accessor: PropTypes.string,
    className: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    renderPhoto: PropTypes.func,
    useBlurredBackground: PropTypes.bool,
  };

  static defaultProps = {
    photoId: '',
    photos: [],
  };

  state = {
    loadingMore: false,
  };

  map = {
    nextPhoto: 'right',
    prevPhoto: 'left',
  };

  componentDidMount() {
    this.checkForFetchMore();
  }

  componentDidUpdate(prevProps) {
    const { photoId } = this.props;

    if (prevProps.photoId !== photoId) {
      this.checkForFetchMore();
    }
  }

  getAdjacentPhotoId = adjacency => {
    const { photoId, photos } = this.props;
    const currentPhotoIndex = photos.findIndex(photo => photo.id === photoId);
    const nextPhoto = photos[currentPhotoIndex + 1];
    const prevPhoto = photos[currentPhotoIndex - 1];

    if (currentPhotoIndex === -1) {
      return undefined;
    }

    if (adjacency === 1 && nextPhoto) {
      return nextPhoto.id;
    }

    if (adjacency === -1 && prevPhoto) {
      return prevPhoto.id;
    }

    return undefined;
  };

  getAdjacentPhotoPath = adjacentPhotoId => {
    const { navigationPath } = this.props;

    const adjacentPhotoPath =
      typeof navigationPath === 'string'
        ? `${navigationPath}/${adjacentPhotoId}`
        : {
            pathname: `${navigationPath.state.basePath}/${adjacentPhotoId}`,
            state: {
              ...navigationPath.state,
            },
          };

    return adjacentPhotoPath;
  };

  changePhoto = adjacency => () => {
    const { getAdjacentPhotoId, history } = this.props;

    if (!history) {
      return;
    }

    const photoId = getAdjacentPhotoId
      ? getAdjacentPhotoId(adjacency)
      : this.getAdjacentPhotoId(adjacency);

    const photoPath = this.getAdjacentPhotoPath(photoId);

    if (photoId) {
      history.replace(photoPath);
    }
  };

  // eslint-disable-next-line react/sort-comp
  handlers = {
    nextPhoto: this.changePhoto(1),
    prevPhoto: this.changePhoto(-1),
  };

  checkForFetchMore = async () => {
    const { photos, photoId, loadMore, totalPhotos } = this.props;

    if (!loadMore || !totalPhotos) {
      return;
    }

    const { loadingMore } = this.state;
    if (loadingMore) {
      return;
    }

    const currentPhotoIndex = photos.findIndex(photo => photo.id === photoId);

    const loadToShowMore =
      currentPhotoIndex !== -1 &&
      photos.length < totalPhotos &&
      photos.length - currentPhotoIndex <= LOAD_MORE_THRESHOLD;

    if (loadToShowMore) {
      this.setState(() => ({ loadingMore: true }));
      await loadMore();
      this.setState(() => ({ loadingMore: false }));
    }
  };

  getCurrentPhotoSrc = () => {
    const {
      photoId,
      photos,
      preferWatermarked,
      maxSize,
      accessor,
      error,
    } = this.props;

    const record = photos.find(photo => photo.id === photoId);

    if (!record || error) {
      return (
        <Alert type="danger">
          The requested photo could not be found. Please try again later.
        </Alert>
      );
    }

    const currentPhoto = accessor ? get(record, accessor) : record;

    const thumbs =
      preferWatermarked && currentPhoto.watermarked_urls
        ? currentPhoto.watermarked_urls
        : currentPhoto.thumbnail_urls;
    const { src } = getImageSources(thumbs, maxSize);

    return src;
  };

  renderCurrentPhoto = () => {
    const {
      photoId,
      photos,
      preferWatermarked,
      maxSize,
      forceImageSize,
      accessor,
      error,
      renderPhoto,
    } = this.props;

    const record = photos.find(photo => photo.id === photoId);

    if (!record || error) {
      return (
        <Alert type="danger">
          The requested photo could not be found. Please try again later.
        </Alert>
      );
    }

    const currentPhoto = accessor ? get(record, accessor) : record;

    const thumbs =
      preferWatermarked && currentPhoto.watermarked_urls
        ? currentPhoto.watermarked_urls
        : currentPhoto.thumbnail_urls;
    const { src, srcSet } = getImageSources(thumbs, maxSize);
    return renderPhoto ? (
      renderPhoto(currentPhoto, {
        albumPhoto: record,
        maxSize,
        preferWatermarked,
        forceImageSize,
      })
    ) : (
      <img
        className="photo-viewer-image"
        src={src}
        srcSet={forceImageSize ? undefined : srcSet}
        alt=""
      />
    );
  };

  render() {
    const {
      photos,
      children,
      getAdjacentPhotoId,
      className,
      useBlurredBackground,
    } = this.props;

    const prevPhotoId = getAdjacentPhotoId
      ? getAdjacentPhotoId(-1)
      : this.getAdjacentPhotoId(-1);
    const nextPhotoId = getAdjacentPhotoId
      ? getAdjacentPhotoId(1)
      : this.getAdjacentPhotoId(1);

    const prevPhotoPath = this.getAdjacentPhotoPath(prevPhotoId);
    const nextPhotoPath = this.getAdjacentPhotoPath(nextPhotoId);
    const src = this.getCurrentPhotoSrc();

    return photos.length ? (
      <HotKeys keyMap={this.map} handlers={this.handlers}>
        <div
          className={classNames({
            'photo-viewer': true,
            [className]: !!className,
          })}
        >
          {useBlurredBackground && src && (
            <div
              className="photo-viewer-blurred-background"
              style={{
                backgroundImage: `url(${src})`,
              }}
            />
          )}
          {children && children}

          <div className="photo-viewer-main">
            {this.renderCurrentPhoto()}

            {prevPhotoId && (
              <Link
                className="photo-viewer-nav photo-viewer-nav-prev"
                to={prevPhotoPath}
                replace
              >
                <ArrowLeft color="#fff" />
              </Link>
            )}

            {nextPhotoId && (
              <Link
                className="photo-viewer-nav photo-viewer-nav-next"
                to={nextPhotoPath}
                replace
              >
                <ArrowLeft color="#fff" />
              </Link>
            )}
          </div>
        </div>
      </HotKeys>
    ) : null;
  }
}

export default PhotoViewer;
